/**
 * Schedule KPI List Row — Two-line informational layout (DEV-840).
 *
 * Mirrors MissionKpiListRow for Schedule progress sub-KPIs:
 *   Line 1: Edit pencil, status dot, name + template badge, weight (editable), Remove
 *   Line 2: Cadence, Role, Permissible-days-late, "Updated Nd ago"
 *
 * Reuses MissionKpiListRow's CSS module — same visual treatment by design.
 * If Schedule rows ever need to diverge visually, extract shared styles to
 * `kpiListRowCommon.module.css` and migrate both consumers.
 */

import React, { useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { SubKpiInstance } from '../../Data/ProjectConfig/projectKpiConfigOverrides';
import styles from './MissionKpiListRow.module.css';

// ── Types ──────────────────────────────────────────────────────

export interface CurrentScore {
  color: 0 | 1 | 2 | 3; // 0=grey, 1=red, 2=yellow, 3=green
  score: number | null;
}

export interface ScheduleKpiListRowProps {
  instance: SubKpiInstance;
  /** Current score + color from the engine. Null when unscored or unavailable. */
  currentScore: CurrentScore | null;
  /** Last reporting date (ISO). Used for freshness. Null if never reported. */
  lastUpdated: string | null;
  /** Cadence period length in days (e.g. 7 for weekly). Drives stale calc. */
  cadenceDays: number;
  /** Grace period (days) past cadence before "stale" flag turns on. */
  gracePeriod: number;
  onEdit: (instance: SubKpiInstance) => void;
  onRemove: (instance: SubKpiInstance) => void;
  onWeightChange: (instance: SubKpiInstance, weight: number) => void;
  /**
   * Reorder hooks. When omitted, the up/down arrows are hidden — useful for
   * lists where reorder isn't supported. canMoveUp/canMoveDown gate the
   * disabled state per row (top can't go up, bottom can't go down).
   */
  onMoveUp?: (instance: SubKpiInstance) => void;
  onMoveDown?: (instance: SubKpiInstance) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

// ── Component ──────────────────────────────────────────────────

export function ScheduleKpiListRow({
  instance,
  currentScore,
  lastUpdated,
  cadenceDays,
  gracePeriod,
  onEdit,
  onRemove,
  onWeightChange,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: ScheduleKpiListRowProps) {
  const { t } = useAppTranslation('projectConfig');

  const weight = instance.weight ?? 100;
  const isMandatory = instance.is_mandatory ?? false;

  // Schedule has one shape; the template variant is the closest analog to
  // Mission's type badge. Show it so users can scan whether SPI is in play.
  const templateLabel = useMemo(() => {
    const tmpl = String((instance as any).schedule_template_type || 'progress');
    return tmpl === 'progress_spi'
      ? t('kpi.templateProgressSpi', 'Progress + SPI')
      : t('kpi.templateProgressOnly', 'Progress Only');
  }, [instance, t]);

  // Compute data freshness — mirror Mission row's calculation exactly.
  const { isStale, daysAgo } = useMemo(() => {
    if (!lastUpdated) return { isStale: false, daysAgo: null as number | null };
    const lastDate = new Date(lastUpdated);
    const today = new Date();
    const diffMs = today.getTime() - lastDate.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const staleThreshold = cadenceDays + gracePeriod;
    return { isStale: days > staleThreshold, daysAgo: days };
  }, [lastUpdated, cadenceDays, gracePeriod]);

  // Status dot color mapping (1=red, 2=yellow, 3=green, 0/null=grey).
  const dotColorClass = useMemo(() => {
    if (!currentScore) return styles.dotGrey;
    switch (currentScore.color) {
      case 3: return styles.dotGreen;
      case 2: return styles.dotYellow;
      case 1: return styles.dotRed;
      default: return styles.dotGrey;
    }
  }, [currentScore]);

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseFloat(e.target.value);
    if (Number.isFinite(next)) onWeightChange(instance, next);
  };

  const handleRemoveClick = () => {
    if (isMandatory) return;
    if (window.confirm(t('kpi.confirmRemove', 'Remove this KPI?'))) {
      onRemove(instance);
    }
  };

  const permissibleDaysLate = (instance as any).permissible_days_late;

  return (
    <div className={styles.row}>
      {/* Line 1 */}
      <div className={styles.line1}>
        <button
          className={styles.editBtn}
          onClick={() => onEdit(instance)}
          title={t('common.edit', 'Edit')}
          aria-label={t('common.edit', 'Edit')}
        >
          ✎
        </button>

        <div className={styles.nameSection}>
          <div className={styles.statusLine}>
            <span className={`${styles.dot} ${dotColorClass}`} />
            <span className={styles.name}>{instance.display_name || 'Schedule KPI'}</span>
            <span className={styles.typeBadge}>
              {isMandatory ? t('kpi.mandatory', 'Mandatory') : t('kpi.custom', 'Custom')}
              {` · ${templateLabel}`}
            </span>
          </div>
        </div>

        <input
          type="number"
          className={styles.weightInput}
          min="0"
          max="100"
          step="1"
          value={weight}
          onChange={handleWeightChange}
          title={t('kpi.weight', 'Weight')}
          aria-label={t('kpi.weight', 'Weight')}
        />

        {/*
          Reorder arrows. Inline-styled so we don't have to extend the shared
          MissionKpiListRow.module.css for a Schedule-only affordance. Mirrors
          the legacy row's ▲/▼ behavior. Hidden when no onMove handlers are
          passed (defensive — list contexts that don't support reorder).
        */}
        {(onMoveUp || onMoveDown) && (
          <div
            style={{
              display: 'inline-flex',
              flexDirection: 'column',
              gap: 1,
              marginInlineEnd: 4,
            }}
          >
            <button
              type="button"
              onClick={() => onMoveUp?.(instance)}
              disabled={!onMoveUp || canMoveUp === false}
              title={t('kpi.moveUp', 'Move up')}
              aria-label={t('kpi.moveUp', 'Move up')}
              style={{
                width: 22,
                height: 14,
                lineHeight: '14px',
                fontSize: 10,
                padding: 0,
                border: '1px solid var(--border-default)',
                borderRadius: 3,
                background: 'var(--bg-hover)',
                color: 'var(--text-primary)',
                cursor: !onMoveUp || canMoveUp === false ? 'not-allowed' : 'pointer',
                opacity: !onMoveUp || canMoveUp === false ? 0.4 : 1,
              }}
            >
              ▲
            </button>
            <button
              type="button"
              onClick={() => onMoveDown?.(instance)}
              disabled={!onMoveDown || canMoveDown === false}
              title={t('kpi.moveDown', 'Move down')}
              aria-label={t('kpi.moveDown', 'Move down')}
              style={{
                width: 22,
                height: 14,
                lineHeight: '14px',
                fontSize: 10,
                padding: 0,
                border: '1px solid var(--border-default)',
                borderRadius: 3,
                background: 'var(--bg-hover)',
                color: 'var(--text-primary)',
                cursor: !onMoveDown || canMoveDown === false ? 'not-allowed' : 'pointer',
                opacity: !onMoveDown || canMoveDown === false ? 0.4 : 1,
              }}
            >
              ▼
            </button>
          </div>
        )}

        <button
          className={styles.removeBtn}
          onClick={handleRemoveClick}
          disabled={isMandatory}
          title={
            isMandatory
              ? t('kpi.mandatoryCannotRemove', 'Mandatory KPIs cannot be removed')
              : t('kpi.remove', 'Remove')
          }
          aria-label={t('kpi.remove', 'Remove')}
        >
          {t('kpi.remove', 'Remove')}
        </button>
      </div>

      {/* Line 2 — informational metadata */}
      <div className={styles.line2}>
        {/* Cadence */}
        <div className={styles.metaItem}>
          <svg className={styles.metaIcon} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="7" r="5" />
            <path d="M7 4v3l2 1.5" />
          </svg>
          <span className={styles.metaLabel}>{t('kpi.cadence', 'Cadence')}:</span>
          <span className={styles.metaValue}>{formatCadence(instance.cadence)}</span>
        </div>

        {/* Role */}
        <div className={styles.metaItem}>
          <svg className={styles.metaIcon} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="5" r="2" />
            <path d="M3 12c0-2 2-3 4-3s4 1 4 3" />
          </svg>
          <span className={styles.metaLabel}>{t('kpi.role', 'Role')}:</span>
          <span className={styles.metaValue}>
            {(instance as any).assigned_role_guid
              ? `${String((instance as any).assigned_role_guid).slice(0, 8)}…`
              : t('kpi.defaultsToOwner', 'Owner')}
          </span>
        </div>

        {/* Permissible days late — Schedule's analog to Mission's deadline */}
        {Number.isFinite(permissibleDaysLate) && (
          <div className={styles.metaItem}>
            <svg className={styles.metaIcon} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="10" height="10" rx="1" />
              <path d="M2 6h10M5 1v3M9 1v3" />
            </svg>
            <span className={styles.metaLabel}>{t('kpi.permissibleDaysLate', 'Days late')}:</span>
            <span className={styles.metaValue}>{permissibleDaysLate}</span>
          </div>
        )}

        {/* Updated freshness */}
        <div className={`${styles.metaItem} ${isStale ? styles.metaStale : ''}`}>
          {daysAgo !== null ? (
            <>
              {t('kpi.updated', 'Updated')} {daysAgo}d {t('kpi.ago', 'ago')}
            </>
          ) : (
            '—'
          )}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────

function formatCadence(cadence?: string): string {
  switch (cadence) {
    case 'daily': return 'Daily';
    case 'weekly': return 'Weekly';
    case 'monthly': return 'Monthly';
    case 'quarterly': return 'Quarterly';
    case 'once': return 'Once';
    default: return cadence || '—';
  }
}

export default ScheduleKpiListRow;
